import { useCallback, useMemo } from 'react';
import { useQueryParamValue } from 'hooks/useQueryParamValue';
import { TERTIARY_NAVIGATION_QUERY_PARAMS } from './tertiaryNavigation.constants';
import type {
  UseTertiaryNavigationQueryParamStateOptions,
  UseTertiaryNavigationQueryParamStateResult,
} from './useTertiaryNavigationQueryParamState.types';

const defaultGetValueFromQueryParam = (queryParamValue: string) => queryParamValue;

// Some tertiary tabs represent page state instead of route hierarchy. This helper only maps query params
// into controlled tertiary-nav props; the page still owns which query value means which tab.
export const useTertiaryNavigationQueryParamState = ({
  defaultValue,
  getValueFromQueryParam = defaultGetValueFromQueryParam,
  // `tab` is the shell-standard query key for active tertiary tabs; override only for domain state.
  queryParamName = TERTIARY_NAVIGATION_QUERY_PARAMS.tab,
  shouldDeleteDefaultValue = true,
}: UseTertiaryNavigationQueryParamStateOptions): UseTertiaryNavigationQueryParamStateResult => {
  const { setValue: setQueryParamValue, value: queryParamValue } = useQueryParamValue({
    paramName: queryParamName,
    shouldDeleteDefaultValue,
  });

  const value = useMemo(
    () => (queryParamValue ? getValueFromQueryParam(queryParamValue) : defaultValue),
    [defaultValue, getValueFromQueryParam, queryParamValue]
  );

  const getItemNavigationProps = useCallback(
    (itemQueryParamValue: string | null) => ({
      onSelect: () => setQueryParamValue(itemQueryParamValue),
    }),
    [setQueryParamValue]
  );

  return {
    getItemNavigationProps,
    queryParamValue,
    setQueryParamValue,
    value,
  };
};
